/**
 * Order callouts via the browser's speech synthesis — Miss Dottie hollering
 * "SCATTERED, SMOTHERED, COVERED!" across the counter. Quality varies by
 * platform; it degrades silently where unsupported.
 */
export class Voice {
  enabled = true;
  private voice: SpeechSynthesisVoice | null = null;
  private readonly synth: SpeechSynthesis | null =
    typeof window !== 'undefined' && 'speechSynthesis' in window ? window.speechSynthesis : null;
  onSpeak: ((seconds: number) => void) | null = null;

  constructor() {
    if (!this.synth) return;
    const pick = (): void => {
      const voices = this.synth?.getVoices() ?? [];
      const english = voices.filter((v) => v.lang.toLowerCase().startsWith('en'));
      const preferred = [
        'Samantha',
        'Aria',
        'Jenny',
        'Google US English',
        'Karen',
        'Moira',
        'Tessa',
      ];
      this.voice =
        preferred.map((name) => english.find((v) => v.name.includes(name))).find(Boolean) ??
        english.find((v) => v.lang === 'en-US') ??
        english[0] ??
        null;
    };
    pick();
    this.synth.addEventListener?.('voiceschanged', pick);
  }

  get supported(): boolean {
    return this.synth !== null;
  }

  /** iOS only allows speech after a user gesture; speak silence to unlock it. */
  unlock(): void {
    if (!this.synth) return;
    try {
      const u = new SpeechSynthesisUtterance(' ');
      u.volume = 0;
      this.synth.speak(u);
    } catch {
      /* ignore */
    }
  }

  say(
    text: string,
    opts: { rate?: number; pitch?: number; interrupt?: boolean; volume?: number } = {},
  ): void {
    if (!this.enabled || !this.synth) return;
    try {
      // Don't let a 3 AM rush build an endless backlog of callouts.
      if (opts.interrupt || this.synth.pending) this.synth.cancel();
      const u = new SpeechSynthesisUtterance(text);
      if (this.voice) u.voice = this.voice;
      u.rate = opts.rate ?? 1.12;
      u.pitch = opts.pitch ?? 1.05;
      u.volume = opts.volume ?? 0.9;
      this.synth.speak(u);
      this.onSpeak?.(Math.min(4, text.length / 14));
    } catch {
      /* speech is a garnish, never a failure */
    }
  }

  cancel(): void {
    try {
      this.synth?.cancel();
    } catch {
      /* ignore */
    }
  }
}
